import {
  AlertTriangle,
  Check,
  ExternalLink,
  Map as MapIcon,
  Plus,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

import { Spinner } from "@/components/data-state";
import { DateTimePicker } from "@/components/date-time-picker";
import { Field } from "@/components/form-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type Step,
  StepperFooter,
  StepperNav,
  useStepper,
} from "@/components/ui/stepper";
import { Switch } from "@/components/ui/switch";
import { useAsync } from "@/hooks/use-async";
import { useAdminCourse, useAdminKey } from "@/hooks/use-auth";
import { MotionNoiseFields } from "@/components/motion-noise-fields";
import { RubricEditor } from "@/components/rubric-editor";
import { RubricTable } from "@/components/rubric-summary";
import type {
  ChallengeType,
  DatasetState,
  Maze,
  MotionNoise,
  RubricCheck,
  RubricSpec,
  SimulatorSide,
  Visibility,
} from "@/lib/api";
import { ApiError, DEFAULT_MOTION_NOISE, adminApi, motionNoiseInvalid } from "@/lib/api";
import { formatTimestamp } from "@/lib/format";
import { suggestedRubric } from "@/lib/scoring";
import { cn } from "@/lib/utils";
import {
  EMPTY_CLIENT_MAZE,
  clientMazeMissing,
  clientMazePayload,
  type ClientMazeInput,
} from "@/legacy/client-maze";
import { ClientMazeStep } from "@/legacy/client-maze-step";
import { viewBox } from "@/routes/admin/mazes/maze-geometry";
import { FLOOR, Markers, Walls } from "@/routes/admin/mazes/maze-svg";
import { NewMazeDialog } from "@/routes/admin/mazes/new-maze-dialog";

/**
 * Options for the enum selects, as data.
 *
 * `items` on the Select root has to be built from the same source that renders the
 * rows -- Base UI's trigger shows the raw value otherwise -- and `hint` keeps the
 * explanation in the dropdown, where there is room for it, rather than in the
 * trigger.
 */
const TYPE_OPTIONS = [
  { value: "development", label: "Practice", hint: "Not graded; students iterate freely" },
  {
    value: "competition",
    label: "Graded",
    hint: "Counts toward the grade; needs the official form link",
  },
] as const;

const VISIBILITY_OPTIONS = [
  {
    value: "staff",
    label: "Staff only",
    hint: "Hidden from students while you prepare it",
  },
  {
    value: "enrolled",
    label: "Enrolled only",
    hint: "Visible to students of this course",
  },
  {
    value: "public",
    label: "Public",
    hint: "Anyone can see it and its leaderboard",
  },
] as const;

/**
 * Server-simulated is the default and the first option. It is the mode every new
 * challenge should use; the client-simulated one exists so the archived terms' way
 * of working can still be set up, and it is labelled as such.
 */
const SIMULATOR_OPTIONS = [
  {
    value: "server",
    label: "On our server",
    hint: "Students connect with the SDK; the maze never leaves this machine",
  },
  {
    value: "client",
    label: "On the student's machine (legacy)",
    hint: "Students run vis_nav_game locally and upload a recording",
  },
] as const;

const DATASET_BADGE: Record<
  DatasetState,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  none: { label: "no dataset", variant: "outline" },
  recording: { label: "recording…", variant: "secondary" },
  done: { label: "dataset ready", variant: "default" },
  failed: { label: "recording failed", variant: "destructive" },
};

function labelMap(options: ReadonlyArray<{ value: string; label: string }>) {
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

function OptionRows({
  options,
}: {
  options: ReadonlyArray<{ value: string; label: string; hint: string }>;
}) {
  return (
    <>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          <span className="flex flex-col items-start">
            <span>{option.label}</span>
            <span className="text-muted-foreground text-xs">{option.hint}</span>
          </span>
        </SelectItem>
      ))}
    </>
  );
}

/**
 * The create-challenge flow.
 *
 * Four steps, in the order a TA actually thinks about it: what it is, which maze
 * it runs in, what students read, and the rules. Everything the challenge needs
 * to be attemptable is collected here -- maze, deadline, and limits included --
 * so that "create" means created, not "created, now go and edit it three times".
 * Only the simulation worker is left for the setup page, because it is a process
 * to start rather than a field to fill.
 */
export function CreateChallengeDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const adminKey = useAdminKey();
  const courseId = useAdminCourse();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    type: "development" as ChallengeType,
    simulator: "server" as SimulatorSide,
    visibility: "enrolled" as Visibility,
    description: "",
    final_submission_link: "",
    // Teams hand in the report on the challenge page (server-simulated only): no form.
    collects_report: true,
    // Server-simulated: which library maze.
    bundle_id: "",
    // Rules.
    deadline: null as number | null,
    // Students get the data and runs from this moment; null opens at creation.
    opens_at: null as number | null,
    // Distance 6/3, heading 2/1, steps 2/1 -- a real grading scheme, not the bare
    // two-threshold default, since a TA who opens this dialog is about to grade something.
    rubric: suggestedRubric("server"),
    show_trajectory: false,
    max_attempts: "" as string, // "" = unlimited
    max_concurrent_sessions: 1,
    max_steps: 60000,
    // On by default: the exploration dataset is recorded noisy, so navigation should be too.
    motion_noise: { ...DEFAULT_MOTION_NOISE, enabled: true } as MotionNoise,
  });
  // Client-simulated only; see src/legacy.
  const [clientMaze, setClientMaze] = useState<ClientMazeInput>(EMPTY_CLIENT_MAZE);
  // The server's reading of the rubric text, from the editor; null until it has answered.
  const [rubricCheck, setRubricCheck] = useState<RubricCheck | null>(null);

  const onServer = form.simulator === "server";
  const graded = form.type === "competition";
  // The report card exists on the server-simulated challenge page only.
  const reportOnSite = onServer && form.collects_report;

  // The library, only for the mode that uses it. Refetchable so a maze made in the
  // nested dialog shows up without closing this one.
  const mazes = useAsync(
    adminKey && onServer ? () => adminApi.mazes(adminKey) : null,
    [adminKey, onServer],
  );
  const chosenMaze = mazes.data?.find((m) => m.id === form.bundle_id) ?? null;

  const attemptsInvalid =
    form.max_attempts.trim() !== "" &&
    (!Number.isInteger(Number(form.max_attempts)) || Number(form.max_attempts) < 1);

  // Five steps, each one question: what is it, where does it run, how is it graded, how
  // much may each student use, and is that all right. The description and the form link
  // sit with the name because they are what students read first; the trajectory switch
  // sits with the maze because it is about disclosing the layout.
  const steps: Step[] = [
    {
      id: "basics",
      title: "Basics",
      hint: "What it is, what students read about it, and where it runs.",
      incomplete: [
        ...(form.name.trim() ? [] : ["a name"]),
        ...(form.description.trim() ? [] : ["a description"]),
        ...(graded && !reportOnSite && !form.final_submission_link.trim()
          ? ["the submission form link, for a graded challenge"]
          : []),
      ],
    },
    {
      id: "maze",
      title: "Maze",
      hint: onServer
        ? "Which library maze the worker serves, and whether students may see it."
        : "The maze file students download, and their dataset.",
      // Students learn the maze from its exploration dataset, so the maze has to have one;
      // the server refuses the create otherwise, and this says so before Next does.
      incomplete: onServer
        ? !form.bundle_id
          ? ["a maze from the library"]
          : chosenMaze && chosenMaze.dataset_state !== "done"
            ? ["a maze whose dataset has been recorded"]
            : []
        : clientMazeMissing(clientMaze),
    },
    {
      id: "scoring",
      title: "Scoring",
      hint: "What a run earns, and for what.",
      incomplete: rubricCheck?.ok ? [] : ["a rubric the server accepts"],
    },
    {
      id: "limits",
      title: "Limits",
      hint: onServer
        ? "The deadline, and how much of the simulator each student may use."
        : "The deadline.",
      incomplete: [
        ...(onServer && form.max_steps < 1 ? ["a step budget above zero"] : []),
        ...(onServer && form.max_concurrent_sessions < 1
          ? ["at least one session at a time"]
          : []),
        ...(onServer && attemptsInvalid ? ["attempts as a whole number, or blank"] : []),
        ...(onServer && motionNoiseInvalid(form.motion_noise)
          ? ["noise sigmas that are zero or positive"]
          : []),
      ],
    },
    {
      id: "confirm",
      title: "Confirm",
      hint: "Everything above, on one page. Most of it can be changed later; the mode and the maze cannot.",
      incomplete: [],
    },
  ];

  const stepper = useStepper(steps);
  const step = stepper.current.id;

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  async function submit() {
    if (!adminKey || !courseId) return;
    if (!onServer && !clientMaze.file) return;
    setBusy(true);
    try {
      const created = await adminApi.createChallenge(
        {
          course_id: courseId,
          name: form.name.trim(),
          type: form.type,
          simulator: form.simulator,
          visibility: form.visibility,
          description: form.description.trim(),
          final_submission_link: reportOnSite ? "" : form.final_submission_link.trim(),
          collects_report: reportOnSite,
          show_trajectory: form.show_trajectory,
          rubric: form.rubric,
          deadline: form.deadline,
          opens_at: form.opens_at,
          ...(onServer
            ? {
                bundle_id: form.bundle_id,
                max_attempts: form.max_attempts.trim()
                  ? Number(form.max_attempts)
                  : null,
                max_concurrent_sessions: form.max_concurrent_sessions,
                max_steps: form.max_steps,
                motion_noise: form.motion_noise.enabled,
                noise_linear_sigma: form.motion_noise.linear_sigma,
                noise_angular_sigma: form.motion_noise.angular_sigma,
                noise_speed_scale_sigma: form.motion_noise.speed_scale_sigma,
                noise_turn_scale_sigma: form.motion_noise.turn_scale_sigma,
                noise_heading_bias_sigma: form.motion_noise.heading_bias_sigma,
              }
            : clientMazePayload(clientMaze)),
        },
        adminKey,
      );
      onCreated();
      onOpenChange(false);
      if (created.simulator === "server") {
        // The row is complete; the only thing left is a process to start.
        toast.success(`Created "${created.name}"`, {
          description: "Start its simulation worker to open it to students.",
        });
        navigate(`/admin/challenges/${created.id}/maze`);
      } else {
        toast.success(`Created "${created.name}"`);
      }
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", {
        duration: 10000,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Header and footer stay put; only the step's form scrolls. The Scoring step is
          taller than a screen, and Next should not be at the bottom of it. */}
      <DialogContent className="flex max-h-[90svh] flex-col gap-0 p-0 sm:max-w-4xl">
        <DialogHeader className="p-4 pb-3">
          <DialogTitle>New challenge</DialogTitle>
          <DialogDescription>{stepper.current.hint}</DialogDescription>
        </DialogHeader>

        {/* Steps down the side, the step's form beside them. */}
        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-4 pb-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-6">
          <div className="sm:sticky sm:top-0 sm:self-start">
            <StepperNav stepper={stepper} orientation="vertical" />
          </div>

          {/* Fixed minimum height so the dialog does not jump between steps. */}
          <div className="grid min-h-[24rem] min-w-0 content-start gap-4">
          {step === "basics" ? (
            <>
              <Field
                id="c-name"
                label="Name"
                required
                hint="Shown to students. The course and term come from the scope above."
              >
                <Input
                  id="c-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Final Challenge"
                  autoFocus
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="c-type" label="Type">
                  <Select
                    items={labelMap(TYPE_OPTIONS)}
                    value={form.type}
                    onValueChange={(v) => {
                      if (typeof v === "string") set("type", v as ChallengeType);
                    }}
                  >
                    <SelectTrigger id="c-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <OptionRows options={TYPE_OPTIONS} />
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="c-visibility" label="Visibility">
                  <Select
                    items={labelMap(VISIBILITY_OPTIONS)}
                    value={form.visibility}
                    onValueChange={(v) => {
                      if (typeof v === "string") set("visibility", v as Visibility);
                    }}
                  >
                    <SelectTrigger id="c-visibility" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <OptionRows options={VISIBILITY_OPTIONS} />
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field
                id="c-sim"
                label="Where the simulation runs"
                hint={
                  <>
                    <span className="text-foreground font-medium">Fixed once created.</span>{" "}
                    Server-simulated is the standard mode: nothing to download, no maze
                    to keep byte-identical, and the step budget is counted here. Choose
                    the legacy mode only to reproduce an earlier term's workflow.
                  </>
                }
              >
                <Select
                  items={labelMap(SIMULATOR_OPTIONS)}
                  value={form.simulator}
                  onValueChange={(v) => {
                    if (typeof v !== "string") return;
                    const next = v as SimulatorSide;
                    setForm((prev) => ({
                      ...prev,
                      simulator: next,
                      // An untouched rubric follows the mode (heading and path only where
                      // the maze has them); an edited one is the TA's to fix.
                      rubric:
                        prev.rubric === suggestedRubric(prev.simulator)
                          ? suggestedRubric(next)
                          : prev.rubric,
                    }));
                  }}
                >
                  <SelectTrigger id="c-sim" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <OptionRows options={SIMULATOR_OPTIONS} />
                  </SelectContent>
                </Select>
              </Field>

              <Field
                id="c-desc"
                label="Description"
                required
                hint="The first thing under the title. Dates, rules of engagement, what to read first."
              >
                <textarea
                  id="c-desc"
                  rows={4}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 w-full resize-y rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-3"
                />
              </Field>

              {onServer ? (
                <div className="flex items-center gap-3 rounded-md border p-3">
                  <Switch
                    id="c-report"
                    checked={form.collects_report}
                    onCheckedChange={(v) => set("collects_report", v)}
                  />
                  <div>
                    <Label htmlFor="c-report">Collect the report on this site</Label>
                    <p className="text-muted-foreground text-xs">
                      Each team rates the difficulty, names a run, and hands in its slides and
                      code on the challenge page, so no submission form is needed. Set the
                      deadline and template in the challenge&apos;s Report section.
                    </p>
                  </div>
                </div>
              ) : null}

              {reportOnSite ? null : (
                <Field
                  id="c-form"
                  label="Official submission form"
                  required={graded}
                  hint={
                    graded
                      ? "Graded challenges are marked off this form, so it cannot be blank."
                      : "Optional for a practice challenge."
                  }
                >
                  <Input
                    id="c-form"
                    value={form.final_submission_link}
                    onChange={(e) => set("final_submission_link", e.target.value)}
                    placeholder="https://forms.google.com/..."
                  />
                </Field>
              )}
            </>
          ) : null}

          {step === "maze" ? (
            <>
              {onServer ? (
                <MazePicker
                  mazes={mazes.data ?? null}
                  loading={mazes.data === null && !mazes.error}
                  error={mazes.error}
                  value={form.bundle_id}
                  onChange={(id) => set("bundle_id", id)}
                  onRefresh={mazes.refetch}
                  adminKey={adminKey ?? ""}
                />
              ) : (
                <ClientMazeStep value={clientMaze} onChange={setClientMaze} />
              )}

              <div className="flex items-center gap-3 rounded-md border p-3">
                <Switch
                  id="c-traj"
                  checked={form.show_trajectory}
                  onCheckedChange={(v) => set("show_trajectory", v)}
                />
                <div>
                  <Label htmlFor="c-traj">Reveal walls and trajectories</Label>
                  <p className="text-muted-foreground text-xs">
                    Lets students replay their runs over the maze. Keep off during a live
                    graded challenge — it discloses the layout. Can be changed later.
                  </p>
                </div>
              </div>
            </>
          ) : null}

          {step === "scoring" && adminKey ? (
            <RubricEditor
              id="c-rubric"
              value={form.rubric}
              onChange={(rubric) => set("rubric", rubric)}
              simulator={form.simulator}
              adminKey={adminKey}
              onChecked={setRubricCheck}
              aside="below"
            />
          ) : null}

          {step === "limits" ? (
            <>
              <Field
                id="c-opens"
                label="Opens at"
                hint="Students see the challenge and a countdown, and get the exploration data and runs from this moment. Staff keys can run it before. Leave blank to open now."
              >
                <DateTimePicker
                  id="c-opens"
                  value={form.opens_at}
                  onChange={(opens_at) => set("opens_at", opens_at)}
                  className="w-full sm:w-80"
                />
              </Field>

              <Field
                id="c-deadline"
                label="Deadline"
                hint="Leave blank for none. Submissions after this are refused; the leaderboard stays readable."
              >
                <DateTimePicker
                  id="c-deadline"
                  value={form.deadline}
                  onChange={(deadline) => set("deadline", deadline)}
                  className="w-full sm:w-80"
                />
              </Field>

              {onServer ? (
                <div className="grid gap-4 rounded-md border p-3">
                  <p className="text-xs font-medium">Session limits</p>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field id="c-attempts" label={graded ? "Attempts, per team" : "Attempts, per student"}>
                      <Input
                        id="c-attempts"
                        type="number"
                        min="1"
                        step="1"
                        value={form.max_attempts}
                        onChange={(e) => set("max_attempts", e.target.value)}
                        placeholder="Unlimited"
                        aria-invalid={attemptsInvalid || undefined}
                      />
                    </Field>
                    <Field id="c-concurrent" label="At once, per team">
                      <Input
                        id="c-concurrent"
                        type="number"
                        min="1"
                        step="1"
                        value={form.max_concurrent_sessions}
                        onChange={(e) =>
                          set("max_concurrent_sessions", Number(e.target.value))
                        }
                      />
                    </Field>
                    <Field id="c-steps" label="Step budget">
                      <Input
                        id="c-steps"
                        type="number"
                        min="1"
                        step="1000"
                        value={form.max_steps}
                        onChange={(e) => set("max_steps", Number(e.target.value))}
                      />
                    </Field>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Blank attempts means unlimited; an aborted or dropped run still spends
                    one. All three can be changed later. For scale, last term&apos;s median
                    successful run took 1,490 steps and the 99th percentile 52,735.
                  </p>
                </div>
              ) : null}

              {onServer ? (
                <MotionNoiseFields
                  id="c-noise"
                  value={form.motion_noise}
                  onChange={(v) => set("motion_noise", v)}
                />
              ) : null}
            </>
          ) : null}

          {step === "confirm" ? (
            <Review
              form={form}
              rubric={rubricCheck?.spec ?? null}
              onServer={onServer}
              mazeName={onServer ? chosenMaze?.name ?? null : clientMaze.file?.name ?? null}
            />
          ) : null}
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 shrink-0">
          <StepperFooter
            stepper={stepper}
            onFinish={submit}
            finishLabel="Create challenge"
            busy={busy}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * Pick a maze from the library, or make one without leaving.
 *
 * A radio list rather than a select: the choice needs three facts per row (size,
 * whether a dataset exists, who else uses it) and a dropdown has room for one.
 * The nested "New maze" dialog is the same one the library page uses, and the
 * maze it returns is selected here as soon as it exists.
 *
 * Only a maze with a recorded dataset will do -- students learn the maze from it --
 * so the others are dimmed. They can still be picked, to see the layout and reach
 * the page where the dataset is recorded; the step just does not complete on one.
 */
function MazePicker({
  mazes,
  loading,
  error,
  value,
  onChange,
  onRefresh,
  adminKey,
}: {
  mazes: Maze[] | null;
  loading: boolean;
  error: unknown;
  value: string;
  onChange: (id: string) => void;
  onRefresh: () => void;
  adminKey: string;
}) {
  const [creating, setCreating] = useState(false);
  const chosen = mazes?.find((m) => m.id === value) ?? null;
  // The listing omits the grid, so the thumbnail is a second, small fetch.
  const detail = useAsync(
    adminKey && value ? () => adminApi.maze(value, adminKey) : null,
    [adminKey, value],
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">
          One maze can serve several challenges — a dry-run and its final, say. It needs a
          recorded dataset; changeable on the challenge page until someone attempts it.
        </p>
        <div className="flex shrink-0 gap-1">
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={onRefresh}
            aria-label="Refresh list"
          >
            <RefreshCw className="size-3.5" aria-hidden />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            <Plus className="size-3.5" aria-hidden />
            New maze
          </Button>
        </div>
      </div>

      {error ? (
        <p className="text-destructive text-sm">
          {error instanceof Error ? error.message : "Could not load the maze library."}
        </p>
      ) : loading ? (
        <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
          <Spinner className="size-4" /> Loading the library…
        </div>
      ) : !mazes || mazes.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-8 text-center">
          <MapIcon className="text-muted-foreground size-6" aria-hidden />
          <p className="text-sm font-medium">The library is empty</p>
          <p className="text-muted-foreground max-w-sm text-xs">
            Generate a maze from a seed — it takes a few seconds and you can preview the
            layout first.
          </p>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-3.5" aria-hidden />
            New maze
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
          <div
            role="radiogroup"
            aria-label="Maze"
            className="max-h-64 divide-y overflow-y-auto rounded-md border"
          >
            {mazes.map((maze) => {
              const selected = maze.id === value;
              const ready = maze.dataset_state === "done";
              const badge = DATASET_BADGE[maze.dataset_state];
              return (
                <button
                  key={maze.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onChange(maze.id)}
                  className={cn(
                    "hover:bg-muted/50 flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
                    selected && "bg-accent/60",
                    !ready && "text-muted-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "grid size-4 shrink-0 place-items-center rounded-full border",
                      selected && "border-primary bg-primary text-primary-foreground",
                    )}
                    aria-hidden
                  >
                    {selected ? <Check className="size-3" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={cn("truncate font-medium", !ready && "font-normal")}>
                        {maze.name}
                      </span>
                      <Badge variant={badge.variant} className="shrink-0 text-[10px]">
                        {badge.label}
                      </Badge>
                    </span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {maze.grid ? `${maze.grid.rows}×${maze.grid.cols}` : "—"}
                      {" · "}
                      {formatTimestamp(maze.created_at)}
                      {maze.used_by.length > 0
                        ? ` · used by ${maze.used_by.map((c) => c.name).join(", ")}`
                        : ""}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="hidden sm:block">
            {chosen ? (
              <div className="space-y-1.5">
                {detail.data?.grid?.cells && detail.data.spawn && detail.data.target ? (
                  <svg
                    viewBox={viewBox(detail.data.grid)}
                    className="aspect-square w-full rounded-md border"
                    style={{ background: FLOOR }}
                    role="img"
                    aria-label={`${chosen.name} layout`}
                  >
                    <Walls grid={detail.data.grid as { rows: number; cols: number; cells: string[] }} />
                    <Markers
                      grid={detail.data.grid}
                      spawn={detail.data.spawn.cell}
                      target={detail.data.target.cell}
                    />
                  </svg>
                ) : (
                  <div className="bg-muted/40 aspect-square animate-pulse rounded-md border" />
                )}
                <Link
                  to={`/admin/mazes/${chosen.id}`}
                  target="_blank"
                  className="text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 text-xs underline-offset-4 hover:underline"
                >
                  Open maze page
                  <ExternalLink className="size-3" aria-hidden />
                </Link>
              </div>
            ) : (
              <div className="text-muted-foreground flex aspect-square items-center justify-center rounded-md border border-dashed p-2 text-center text-xs">
                Select a maze to preview it
              </div>
            )}
          </div>
        </div>
      )}

      {chosen && chosen.dataset_state !== "done" ? (
        <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5 text-xs">
          <AlertTriangle className="size-4 shrink-0 text-amber-600" aria-hidden />
          <span>
            {chosen.dataset_state === "recording" ? (
              <>
                <span className="font-medium">This maze&apos;s dataset is being recorded.</span>{" "}
                Refresh the list when it finishes; until then the challenge cannot be created
                in it.
              </>
            ) : (
              <>
                <span className="font-medium">
                  {chosen.dataset_state === "failed"
                    ? "This maze's last recording failed."
                    : "This maze has no exploration dataset yet."}
                </span>{" "}
                Students learn the maze from it, so a challenge cannot be created here until
                one is recorded.{" "}
                <Link
                  to={`/admin/mazes/${chosen.id}`}
                  target="_blank"
                  className="font-medium underline underline-offset-4"
                >
                  Record it on the maze page
                </Link>
                , then refresh the list.
              </>
            )}
          </span>
        </p>
      ) : null}

      <NewMazeDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(maze) => {
          onRefresh();
          onChange(maze.id);
        }}
      />
    </div>
  );
}

function Review({
  form,
  rubric,
  onServer,
  mazeName,
}: {
  form: {
    name: string;
    type: ChallengeType;
    visibility: Visibility;
    description: string;
    final_submission_link: string;
    collects_report: boolean;
    show_trajectory: boolean;
    deadline: number | null;
    opens_at: number | null;
    max_attempts: string;
    max_concurrent_sessions: number;
    max_steps: number;
    rubric: string;
    motion_noise: MotionNoise;
  };
  rubric: RubricSpec | null;
  onServer: boolean;
  mazeName: string | null;
}) {
  return (
    <dl className="grid gap-x-4 gap-y-2 rounded-md border p-3 text-xs sm:grid-cols-[7rem_1fr]">
      <dt className="text-muted-foreground">Name</dt>
      <dd className="truncate font-medium">{form.name || "—"}</dd>
      <dt className="text-muted-foreground">Type</dt>
      <dd>
        {form.type === "competition" ? "Graded" : "Practice"} ·{" "}
        {form.visibility === "public"
          ? "public"
          : form.visibility === "staff"
            ? "staff only"
            : "enrolled only"}{" "}
        ·{" "}
        {onServer ? "on our server" : "on the student's machine"}
      </dd>
      <dt className="text-muted-foreground">Description</dt>
      <dd className="line-clamp-3 whitespace-pre-line">{form.description || "—"}</dd>
      {onServer && form.collects_report ? (
        <>
          <dt className="text-muted-foreground">Report</dt>
          <dd>Handed in on the challenge page</dd>
        </>
      ) : form.final_submission_link.trim() ? (
        <>
          <dt className="text-muted-foreground">Report form</dt>
          <dd className="truncate">{form.final_submission_link}</dd>
        </>
      ) : null}
      <dt className="text-muted-foreground">Maze</dt>
      <dd className="truncate">
        {mazeName ?? "—"}
        <span className="text-muted-foreground">
          {" · "}
          {form.show_trajectory ? "walls and trajectories shown" : "layout hidden"}
        </span>
      </dd>
      <dt className="text-muted-foreground">Scoring</dt>
      <dd>
        {rubric ? (
          <RubricTable spec={rubric} />
        ) : (
          <pre className="font-mono whitespace-pre-wrap">{form.rubric}</pre>
        )}
      </dd>
      <dt className="text-muted-foreground">Deadline</dt>
      <dd>{form.deadline !== null ? formatTimestamp(form.deadline) : "none"}</dd>
      <dt className="text-muted-foreground">Opens</dt>
      <dd>{form.opens_at !== null ? formatTimestamp(form.opens_at) : "now"}</dd>
      {onServer ? (
        <>
          <dt className="text-muted-foreground">Limits</dt>
          <dd>
            {form.max_attempts.trim() ? `${form.max_attempts} attempts` : "unlimited attempts"}
            {" · "}
            {form.max_concurrent_sessions} at once · {form.max_steps.toLocaleString()} steps
          </dd>
          <dt className="text-muted-foreground">Motion</dt>
          <dd>
            {form.motion_noise.enabled
              ? `noisy · σ ${form.motion_noise.linear_sigma} m/s, ${form.motion_noise.angular_sigma} rad/s`
              : "exact"}
          </dd>
        </>
      ) : null}
    </dl>
  );
}
